import { useEffect, useId, useMemo, useState } from "react";

import directorApi from "@contexts/director/api";

import { formatBps } from "../../../utils/directorFormat";
import { formatCents as formatMoneyInput, parseBRL } from "../costPlanning/money";

// Premissas do diretor (SPEC 2026-10-21 §3.6). O painel só converte unidades (% ↔ bps,
// R$ ↔ centavos) e espelha as regras do BE para dar retorno imediato; quem valida de fato e
// calcula os indicadores é o BE.

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const INTEGER = /^\d+$/;
const PERCENT = /^\d{1,3}(?:[.,]\d{1,2})?$/;
// O rótulo já diz "(%)", mas quem digita "12%" ou "12 %" quer dizer 12.
const stripPercent = (text) => text.trim().replace(/\s*%$/, "");
const MAX_REASON_LENGTH = 500;

const CONDITION_PERCENTS = [
  ["signal", "signalBps", "Sinal (%)"],
  ["monthly", "monthlyBps", "Mensais até as chaves (%)"],
  ["keys", "keysBps", "Chaves (%)"],
  ["postKeys", "postKeysBps", "Pós-chaves (%)"],
];
const CONDITION_FIELDS = [...CONDITION_PERCENTS.map(([field]) => field), "postKeysMonths", "keysMonth"];

const REQUIRED_IN_CONDITION = "Obrigatório quando a condição é informada.";
const MONTH_ERROR = "Informe o mês no formato AAAA-MM.";
const MONEY_ERROR = "Informe o valor em reais, ex.: 120.000.000,00.";
const SUCCESS = "Premissas salvas. O dashboard foi recalculado.";
const SAVE_FAILED = "Não foi possível salvar as premissas. Tente novamente.";
const LOAD_FAILED = "Não foi possível carregar as premissas.";
const READ_ONLY = "Só quem tem acesso a todas as obras da empresa pode alterar as premissas.";

const dateFormatter = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

// A soma usa duas casas: com uma, 9999 bps (33,33 × 3) apareceria como "100%" e contradiria o bloqueio.
const sumFormatter = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const formatSum = (bps) => `${sumFormatter.format(bps / 100)}%`;

const blank = (text) => typeof text !== "string" || text.trim() === "";

// --- bps/centavos → texto do campo ---------------------------------------------------------

const bpsToInput = (bps) => (Number.isSafeInteger(bps) ? String(bps / 100).replace(".", ",") : "");
const centsToInput = (cents) => (Number.isSafeInteger(cents) && cents >= 0 ? formatMoneyInput(cents) : "");
const intToInput = (value) => (Number.isSafeInteger(value) ? String(value) : "");
const monthToInput = (value) => (typeof value === "string" && MONTH.test(value) ? value : "");

const obraFormFrom = (project) => {
  const condition = project.paymentCondition || {};
  return {
    stock: centsToInput(project.stockCents),
    salesMonths: intToInput(project.salesMonths),
    salesStart: monthToInput(project.salesStart),
    ...Object.fromEntries(CONDITION_PERCENTS.map(([field, key]) => [field, bpsToInput(condition[key])])),
    postKeysMonths: intToInput(condition.postKeysMonths),
    keysMonth: monthToInput(condition.keysMonth),
    land: centsToInput(project.landCents),
    landMonth: monthToInput(project.landMonth),
  };
};

export const formFromPremises = (premises) => ({
  discountRate: bpsToInput(premises?.discountRateBps ?? 1200),
  tax: bpsToInput(premises?.taxBps ?? 0),
  reason: "",
  projects: Object.fromEntries((premises?.projects || [])
    .filter((project) => typeof project?.projectKey === "string")
    .map((project) => [project.projectKey, obraFormFrom(project)])),
});

// --- texto do campo → bps/centavos (`{ value }` ou `{ error }`) ---------------------------

const parsePercent = (text, maxBps, error) => {
  const value = stripPercent(text);
  if (!PERCENT.test(value)) return { error };
  const bps = Math.round(Number(value.replace(",", ".")) * 100);
  return bps <= maxBps ? { value: bps } : { error };
};

const parseMoney = (text) => {
  try {
    const cents = parseBRL(text);
    return cents === null ? { error: MONEY_ERROR } : { value: cents };
  } catch {
    return { error: MONEY_ERROR };
  }
};

const parseInteger = (text, min, max, error) => {
  const value = text.trim();
  if (!INTEGER.test(value)) return { error };
  const number = Number(value);
  return Number.isSafeInteger(number) && number >= min && number <= max ? { value: number } : { error };
};

const parseMonth = (text) => (MONTH.test(text.trim()) ? { value: text.trim() } : { error: MONTH_ERROR });

// Uma obra: campos em branco ficam de fora; a condição, se tocada, exige os seis campos.
const buildObra = (projectKey, fields = {}) => {
  const errors = {};
  const project = { projectKey };
  const take = (field, key, parse) => {
    if (blank(fields[field])) return;
    const result = parse(fields[field]);
    if (result.error) errors[field] = result.error;
    else project[key] = result.value;
  };

  take("stock", "stockCents", parseMoney);
  take("salesMonths", "salesMonths", (text) => parseInteger(text, 1, 240, "Informe de 1 a 240 meses."));
  take("salesStart", "salesStart", parseMonth);

  let conditionSumBps = null;
  if (CONDITION_FIELDS.some((field) => !blank(fields[field]))) {
    const condition = {};
    const required = (field, parse) => {
      if (blank(fields[field])) { errors[field] = REQUIRED_IN_CONDITION; return undefined; }
      const result = parse(fields[field]);
      if (result.error) { errors[field] = result.error; return undefined; }
      return result.value;
    };
    const percents = CONDITION_PERCENTS.map(([field, key]) => {
      const value = required(field, (text) => parsePercent(text, 10000, "Informe um percentual entre 0 e 100."));
      if (value !== undefined) condition[key] = value;
      return value;
    });
    conditionSumBps = percents.includes(undefined) ? null : percents.reduce((total, value) => total + value, 0);
    const postKeysMonths = required("postKeysMonths", (text) => parseInteger(text, 0, 360, "Informe de 0 a 360 meses."));
    const keysMonth = required("keysMonth", parseMonth);
    if (conditionSumBps !== null && conditionSumBps !== 10000) errors.conditionSum = "A condição precisa somar 100%.";
    if (!Object.keys(errors).some((field) => CONDITION_FIELDS.includes(field) || field === "conditionSum")) {
      project.paymentCondition = { ...condition, postKeysMonths, keysMonth };
    }
  }

  take("land", "landCents", parseMoney);
  take("landMonth", "landMonth", parseMonth);
  // Sem mês, o BE ignora o terreno: melhor avisar do que salvar um valor que não entra.
  if (!blank(fields.land) && !errors.land && project.landCents > 0 && blank(fields.landMonth)) {
    errors.landMonth = "Informe o mês do terreno.";
  }

  return { project: Object.keys(project).length > 1 ? project : null, errors, conditionSumBps };
};

// Corpo do PUT /director/premises. `projects` vai sempre: omitir apaga as premissas por obra.
export const buildPremisesBody = (form, projectKeys, expectedVersion) => {
  const errors = { company: {}, projects: {} };
  const sums = {};
  const rate = blank(form.discountRate)
    ? { error: "Informe a taxa de desconto." }
    : parsePercent(form.discountRate, 10000, "Informe a taxa entre 0% e 100%.");
  const tax = blank(form.tax)
    ? { error: "Informe os impostos (0% se não houver)." }
    : parsePercent(form.tax, 5000, "Informe os impostos entre 0% e 50%.");
  if (rate.error) errors.company.discountRate = rate.error;
  if (tax.error) errors.company.tax = tax.error;

  const projects = [];
  projectKeys.forEach((projectKey) => {
    const obra = buildObra(projectKey, form.projects[projectKey]);
    sums[projectKey] = obra.conditionSumBps;
    if (Object.keys(obra.errors).length) errors.projects[projectKey] = obra.errors;
    else if (obra.project) projects.push(obra.project);
  });

  const reason = (form.reason || "").trim();
  if (reason.length > MAX_REASON_LENGTH) errors.reason = `O motivo deve ter até ${MAX_REASON_LENGTH} caracteres.`;

  const valid = !Object.keys(errors.company).length && !Object.keys(errors.projects).length && !errors.reason;
  const body = valid
    ? {
      expectedVersion,
      discountRateBps: rate.value,
      taxBps: tax.value,
      projects,
      ...(reason ? { reason } : {}),
    }
    : null;
  return { body, errors, sums, valid };
};

// --- apresentação ---------------------------------------------------------------------------

const statusLine = (premises) => {
  if (!premises?.version) {
    return "Nenhuma premissa informada ainda. O dashboard está mostrando só o que o Sienge sabe.";
  }
  const name = premises.updatedBy?.name;
  const date = typeof premises.updatedAt === "string" && !Number.isNaN(Date.parse(premises.updatedAt))
    ? dateFormatter.format(new Date(premises.updatedAt))
    : null;
  return `Última atualização${name ? ` por ${name}` : ""}${date ? ` em ${date}` : ""}`;
};

const portfolioLine = (mix) => {
  const parts = (Array.isArray(mix) ? mix : [])
    .filter((item) => Number.isSafeInteger(item?.shareBps) && item?.name)
    .map((item) => `${formatBps(item.shareBps)} ${item.name}`);
  return parts.length ? `Carteira atual: ${parts.join(" · ")}` : null;
};

const errorMessage = (result) => {
  const message = typeof result?.body?.message === "string" ? result.body.message : null;
  if (result?.status === 401) return "Faça login novamente para salvar as premissas.";
  if ([403, 409, 422].includes(result?.status) && message) return message;
  return SAVE_FAILED;
};

const Field = ({ id, label, value, onChange, error, readOnly, type = "text", inputMode, hint, wide }) => <div
  className={`director-premises__field${wide ? " director-premises__field--wide" : ""}`}
>
  <label className="director-premises__label" htmlFor={id}>{label}</label>
  <input
    className="director-premises__input"
    id={id}
    type={type}
    inputMode={inputMode}
    value={value}
    readOnly={readOnly}
    aria-invalid={error ? "true" : undefined}
    aria-describedby={error ? `${id}-error` : undefined}
    onChange={(event) => onChange(event.target.value)}
  />
  {hint && <small className="director-premises__hint">{hint}</small>}
  {error && <small className="director-premises__error" id={`${id}-error`}>{error}</small>}
</div>;

const ObraFields = ({ obra, fields, errors = {}, sumBps, readOnly, prefix, onChange }) => {
  const id = (field) => `${prefix}-${obra.projectKey}-${field}`;
  const value = (field) => fields?.[field] ?? "";
  const field = (name, label, props = {}) => <Field
    key={name}
    id={id(name)}
    label={label}
    value={value(name)}
    error={errors[name]}
    readOnly={readOnly}
    onChange={(text) => onChange(obra.projectKey, name, text)}
    {...props}
  />;
  const conditionTouched = CONDITION_FIELDS.some((name) => !blank(fields?.[name]));
  const portfolio = portfolioLine(obra.paymentTermMix);

  return <fieldset className="director-premises__obra">
    <legend className="director-premises__legend">{obra.name || obra.projectKey}</legend>
    <div className="director-premises__grid">
      {field("stock", "Estoque a vender (R$)", { inputMode: "decimal" })}
      {field("salesMonths", "Meses de venda", { inputMode: "numeric" })}
      {field("salesStart", "Início das vendas", { type: "month" })}
    </div>
    <p className="director-premises__hint">
      Sem meses e início, a venda do estoque é distribuída em 24 meses a partir do próximo mês.
    </p>
    <div className="director-premises__condition">
      <p className="director-premises__subtitle">Condição de pagamento</p>
      {portfolio && <p className="director-premises__portfolio">{portfolio}</p>}
      <div className="director-premises__grid">
        {CONDITION_PERCENTS.map(([name, , label]) => field(name, label, { inputMode: "decimal" }))}
        {field("postKeysMonths", "Parcelas pós-chaves (meses)", { inputMode: "numeric" })}
        {field("keysMonth", "Mês das chaves", { type: "month" })}
      </div>
      {conditionTouched && <p
        className="director-premises__sum"
        data-tone={sumBps === 10000 ? "good" : "bad"}
        aria-live="polite"
      >
        {sumBps === null ? "Soma: —" : `Soma: ${formatSum(sumBps)}`}
      </p>}
      {errors.conditionSum && <p className="director-premises__error" role="alert">{errors.conditionSum}</p>}
    </div>
    <div className="director-premises__grid">
      {field("land", "Terreno fora do Sienge (R$)", { inputMode: "decimal" })}
      {field("landMonth", "Mês do terreno", { type: "month" })}
    </div>
  </fieldset>;
};

const PremisesPanelBody = ({ companyId, obras, onSaved, api = directorApi }) => {
  const prefix = `director-premises${useId().replace(/:/g, "-")}`;
  const [revision, setRevision] = useState(0);
  const [load, setLoad] = useState({ status: "loading", premises: null, canWrite: false });
  const [form, setForm] = useState(() => formFromPremises(null));
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  // A lista de obras vem do dashboard, que zera no reload: guarda a última conhecida.
  const [knownObras, setKnownObras] = useState(() => (Array.isArray(obras) ? obras : []));

  useEffect(() => {
    if (Array.isArray(obras)) setKnownObras(obras);
  }, [obras]);

  useEffect(() => {
    let active = true;
    if (!companyId) return () => { active = false; };
    setLoad((current) => ({ ...current, status: "loading" }));
    Promise.resolve(api.fetchDirectorPremises(companyId)).then((result) => {
      if (!active) return;
      const premises = result?.ok ? result.body?.premises : null;
      if (!premises) {
        setLoad({ status: "error", premises: null, canWrite: false });
        return;
      }
      setLoad({ status: "ready", premises, canWrite: result.body.canWrite === true });
      setForm(formFromPremises(premises));
    }).catch(() => {
      if (active) setLoad({ status: "error", premises: null, canWrite: false });
    });
    return () => { active = false; };
  }, [api, companyId, revision]);

  const { premises, canWrite } = load;
  const readOnly = !canWrite;

  // Obras do dashboard e, depois, as que só existem nas premissas (sem nome, pela chave).
  const obraList = useMemo(() => {
    const seen = new Set();
    const fresh = (key) => typeof key === "string" && !seen.has(key) && Boolean(seen.add(key));
    const listed = knownObras.filter((obra) => fresh(obra?.projectKey));
    const extra = (premises?.projects || [])
      .filter((project) => fresh(project?.projectKey))
      .map((project) => ({ projectKey: project.projectKey, name: null, paymentTermMix: null }));
    return [...listed, ...extra];
  }, [knownObras, premises]);

  const projectKeys = useMemo(() => obraList.map((obra) => obra.projectKey), [obraList]);
  const built = useMemo(
    () => buildPremisesBody(form, projectKeys, premises?.version ?? 0),
    [form, projectKeys, premises],
  );

  const setCompany = (field) => (text) => setForm((current) => ({ ...current, [field]: text }));
  const setObraField = (projectKey, field, text) => setForm((current) => ({
    ...current,
    projects: { ...current.projects, [projectKey]: { ...current.projects[projectKey], [field]: text } },
  }));

  const save = async (event) => {
    event.preventDefault();
    if (!built.valid || saving || readOnly) return;
    setSaving(true);
    setNotice(null);
    let result;
    try {
      result = await api.saveDirectorPremises(companyId, built.body);
    } catch {
      result = null;
    }
    setSaving(false);
    if (result?.ok && result.body?.premises) {
      setLoad({ status: "ready", premises: result.body.premises, canWrite: result.body.canWrite === true });
      setForm(formFromPremises(result.body.premises));
      setNotice({ tone: "success", text: SUCCESS });
      onSaved?.();
      return;
    }
    setNotice({ tone: "error", text: errorMessage(result) });
    if (result?.status === 409) setRevision((value) => value + 1);
  };

  if (load.status === "error") {
    return <div className="director-premises">
      <p className="director-premises__notice" data-tone="error" role="alert">{LOAD_FAILED}</p>
      <button
        className="director-link-button director-link-button--secondary director-premises__retry"
        type="button"
        onClick={() => setRevision((value) => value + 1)}
      >
        Tentar de novo
      </button>
    </div>;
  }

  if (!premises) {
    return <div className="director-premises">
      <p className="director-premises__status" role="status">Carregando premissas…</p>
    </div>;
  }

  return <form className="director-premises" onSubmit={save} noValidate>
    <p className="director-premises__status">{statusLine(premises)}</p>
    {premises.version > 0 && premises.reason && <p className="director-premises__hint">Motivo: {premises.reason}</p>}
    {readOnly && <p className="director-premises__hint">{READ_ONLY}</p>}
    {notice && <p
      className="director-premises__notice"
      data-tone={notice.tone}
      role={notice.tone === "error" ? "alert" : "status"}
    >
      {notice.text}
    </p>}

    <fieldset className="director-premises__company">
      <legend className="director-premises__legend">Da empresa</legend>
      <div className="director-premises__grid">
        <Field
          id={`${prefix}-discount-rate`}
          label="Taxa de desconto (% a.a.)"
          value={form.discountRate}
          error={built.errors.company.discountRate}
          readOnly={readOnly}
          inputMode="decimal"
          hint={premises.provisional ? "taxa provisória" : null}
          onChange={setCompany("discountRate")}
        />
        <Field
          id={`${prefix}-tax`}
          label="Impostos sobre recebimentos futuros (%)"
          value={form.tax}
          error={built.errors.company.tax}
          readOnly={readOnly}
          inputMode="decimal"
          onChange={setCompany("tax")}
        />
      </div>
    </fieldset>

    {obraList.map((obra) => <ObraFields
      key={obra.projectKey}
      obra={obra}
      fields={form.projects[obra.projectKey]}
      errors={built.errors.projects[obra.projectKey]}
      sumBps={built.sums[obra.projectKey]}
      readOnly={readOnly}
      prefix={prefix}
      onChange={setObraField}
    />)}

    {!readOnly && <div className="director-premises__footer">
      <div className="director-premises__field director-premises__field--wide">
        <label className="director-premises__label" htmlFor={`${prefix}-reason`}>Motivo (opcional)</label>
        <textarea
          className="director-premises__input director-premises__textarea"
          id={`${prefix}-reason`}
          value={form.reason}
          maxLength={MAX_REASON_LENGTH}
          onChange={(event) => setCompany("reason")(event.target.value)}
        />
        {built.errors.reason && <small className="director-premises__error">{built.errors.reason}</small>}
      </div>
      <button
        className="director-link-button director-premises__save"
        type="submit"
        disabled={!built.valid || saving}
      >
        {saving ? "Salvando…" : "Salvar premissas"}
      </button>
      {premises.provisional && !built.errors.company.discountRate && <p className="director-premises__hint">
        {`A taxa de ${stripPercent(form.discountRate)}% a.a. passa a valer como taxa da empresa ao salvar.`}
      </p>}
    </div>}
  </form>;
};

// Trocar de empresa remonta o painel: form, aviso e obras da empresa anterior não podem ficar
// na tela (nem num Salvar) enquanto o GET da nova empresa não responde.
const PremisesPanel = (props) => <PremisesPanelBody key={props.companyId ?? ""} {...props} />;

export default PremisesPanel;
